import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Avatar,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
} from '@mui/material'
import {
  Timer as TimerIcon,
  PlayArrow as PlayArrowIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Keyboard as KeyboardIcon,
  LocationOn,
  CalendarToday,
  Edit,
  Delete,
  Navigation,
  Lock,
  Person as PersonIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Replay as ReplayIcon,
  Science as ScienceIcon,
  Star as StarIcon,
  RateReview as RateReviewIcon,
  People as PeopleIcon,
  HelpOutline as HelpOutlineIcon,
  SupportAgent as SupportAgentIcon,
  Description as DescriptionIcon,
  Forum as ForumIcon,
  EmojiEvents as LeaderboardIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material'
import { useJoinAdventureSolo, useDeleteAdventure, useAdventureCategories } from '../hooks/useAdventures'
import { useActiveSessionForAdventure } from '../hooks/useSession'
import { GroupPlayDialog } from '../components/GroupPlayDialog'
import { AdventureLeaderboard } from '../components/AdventureLeaderboard'
import { RecentCheckins } from '../components/RecentCheckins'
import { AdventureDiscussions } from '../components/AdventureDiscussions'
import type { AdventureJoinResponse } from '@api/types/adventure'
import { useAuthStore } from '@store/authStore'
import { useGeolocation } from '@hooks/useGeolocation'
import { navigateTo } from '@utils/navigation'
import { LazyMap } from '@components/map/LazyMap'
import { getAdventureStatusLabel, getAdventureStatusColor, AdventureMainCategory, AdventureStatus, isTrailCategory } from '../types/adventure-editor.types'
import { Marker, Popup, Circle } from 'react-leaflet'
import L from 'leaflet'
import { useTags } from '@contexts/TagsContext'
import { AdventureDto, StatisticTypes } from '@api/types/adventure'
import { hasJoinedAdventure, SessionStatus, UserRelationType } from '@api/types/session'
import { ratingsApi } from '@api/endpoints/ratings'
import { profileApi } from '@api/endpoints/profile'
import { UserProfileDto } from '@/types/profile'
import { RatingDto, RatingDetailedDto, RatingTargetType, RATING_CATEGORIES } from '@api/types/ratings'
import { createTicket } from '@api/services/tickets.api'
import { TargetType, TicketType, TicketPriority, TicketTypeLabels, TicketPriorityLabels } from '@api/types/tickets'
import { TicketTypeIcon } from '@features/tickets/components/TicketTypeIcon'
import { TicketPriorityIndicator } from '@features/tickets/components/TicketPriorityIndicator'
import { adventuresApi } from '@api/endpoints/adventures'
import { downloadGpx, type GpxExportOptions } from '../utils/gpxGenerator'
import type { RouteTravelMode } from '../types/workflow.types'

// Create a custom icon for user location
const userLocationIcon = new L.DivIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: #2196F3;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      position: relative;
    ">
      <div style="
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

interface AdventureDetailViewProps {
  adventure: AdventureDto
}

export default function AdventureDetailView({ adventure }: AdventureDetailViewProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // Show details as tab (not sidebar) below 960px
  const showDetailsAsTab = useMediaQuery('(max-width: 959px)')
  const soloJoinMutation = useJoinAdventureSolo()
  const deleteMutation = useDeleteAdventure()
  const { location: userLocation } = useGeolocation()
  const { getTags } = useTags()

  // Fetch adventure categories to determine join method
  const { data: adventureCategories } = useAdventureCategories()

  // Find the category settings for this adventure
  const categorySettings = adventureCategories?.find(
    (cat) => cat.id === (adventure.mainCategory ?? (adventure as any).categoryId)
  )

  // QR Code criteria ID - if this is the only allowed join criteria, show "Scan Code" button
  const QR_CODE_CRITERIA_ID = 402

  // Check if QR code is the only join method for this category
  const isQrCodeOnlyJoin = categorySettings?.allowedJoinCriteria?.length === 1 &&
    categorySettings.allowedJoinCriteria[0] === QR_CODE_CRITERIA_ID

  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false)
  const [startConfirmDialogOpen, setStartConfirmDialogOpen] = useState(false)
  const [codeEntryDialogOpen, setCodeEntryDialogOpen] = useState(false)
  const [enteredCode, setEnteredCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [moderationWarningOpen, setModerationWarningOpen] = useState(false)
  const [pendingPlayAction, setPendingPlayAction] = useState<'solo' | 'group' | null>(null)

  // Rating state - multi-score system
  const [userRating, setUserRating] = useState<RatingDto | null>(null)
  const [isLoadingRating, setIsLoadingRating] = useState(false)
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false)
  const [hasCheckedRating, setHasCheckedRating] = useState(false)
  const [ratingScores, setRatingScores] = useState({
    presenceScore: 0,
    obscuraScore: 0,
    trailcraftScore: 0,
    craftmarkScore: null as number | null,
  })
  const [ratingComment, setRatingComment] = useState('')
  const [containsSpoilers, setContainsSpoilers] = useState(false)
  const [isSubmittingRating, setIsSubmittingRating] = useState(false)
  const [ratingError, setRatingError] = useState<string | null>(null)
  const [ratingSuccess, setRatingSuccess] = useState(false)

  // Rating display toggle: 'average' or 'user'
  const [ratingViewMode, setRatingViewMode] = useState<'average' | 'user'>('average')

  // All ratings dialog state
  const [allRatingsDialogOpen, setAllRatingsDialogOpen] = useState(false)
  const [allRatings, setAllRatings] = useState<RatingDetailedDto[]>([])
  const [allRatingsLoading, setAllRatingsLoading] = useState(false)
  const [allRatingsPage, setAllRatingsPage] = useState(1)
  const [allRatingsTotalPages, setAllRatingsTotalPages] = useState(1)
  const [allRatingsTotalCount, setAllRatingsTotalCount] = useState(0)
  const [allRatingsSortBy, setAllRatingsSortBy] = useState<'CreatedAt' | 'PresenceScore' | 'ObscuraScore' | 'TrailcraftScore'>('CreatedAt')
  const [allRatingsSortOrder, setAllRatingsSortOrder] = useState<'asc' | 'desc'>('desc')

  // Contact creator dialog state
  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [subscriptionRequiredOpen, setSubscriptionRequiredOpen] = useState(false)
  const [contactMessage, setContactMessage] = useState('')
  const [contactTicketType, setContactTicketType] = useState<TicketType>(TicketType.Question)
  const [contactPriority, setContactPriority] = useState<TicketPriority>(TicketPriority.Medium)
  const [isSubmittingContact, setIsSubmittingContact] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [contactSuccess, setContactSuccess] = useState(false)

  // Content tab state: 'description' | 'discussion' | 'leaderboard' | 'details'
  const [activeContentTab, setActiveContentTab] = useState<'description' | 'discussion' | 'leaderboard' | 'details'>('description')

  // GPX export state
  const [isExportingGpx, setIsExportingGpx] = useState(false)
  const [gpxExportError, setGpxExportError] = useState<string | null>(null)

  // Creator profile state
  const [creatorProfile, setCreatorProfile] = useState<UserProfileDto | null>(null)

  // Check if this is a geocache type adventure
  // Backend may return 0 or 1 for TraditionalGeoCaches depending on enum indexing
  const isGeocache = adventure.mainCategory === 0 ||
    adventure.mainCategory === AdventureMainCategory.TraditionalGeoCaches

  // Check if this is a trail type adventure (Trailcraft-only rating)
  const isTrail = isTrailCategory(adventure.mainCategory)

  // Check if this is a POI adventure
  const isPOI = adventure.mainCategory === AdventureMainCategory.PlaceOfInterest

  // Check if this is a silent adventure (no ratings for silent adventures)
  const isSilentAdventure = adventure.mainCategory === AdventureMainCategory.SilentAdventure

  // Check if user has joined this adventure
  const hasJoined = hasJoinedAdventure(adventure.userRelationType)

  // Tester / test-mode helpers. Hoisted above the useEffects that reference isTestMode.
  // canTest: user is a tester/maintainer/moderator/owner and may exercise the Testing
  //   workflow version.
  // isTestMode: the adventure is currently surfaced as "Testing" to this user, either
  //   because its top-level status is Testing or because a Testing workflow version
  //   exists and the user can access it.
  const userRelation = adventure.userRelationType ?? 0
  const isTester = (userRelation & UserRelationType.Tester) !== 0
  const isMaintainer = (userRelation & UserRelationType.Maintainer) !== 0
  const isAdventureModerator = (userRelation & UserRelationType.Moderator) !== 0
  const isAdventureOwner = user?.id === (adventure.owner?.entityId || adventure.createdBy)
  const canTest = isTester || isMaintainer || isAdventureModerator || isAdventureOwner
  const isTestMode =
    adventure.status === AdventureStatus.Testing ||
    (adventure.hasTestVersion === true && canTest)

  // Check for active session (only if user has joined AND adventure is not already completed)
  // No need to check for sessions if the adventure is already completed
  const shouldCheckForSession = hasJoined && !adventure.isCompleted
  const {
    data: activeSession,
    isLoading: checkingSession,
  } = useActiveSessionForAdventure(adventure.id, { enabled: shouldCheckForSession })

  // Fetch user's rating when adventure is completed (skip for silent adventures and
  // for test-mode completions — a tester running the Testing workflow shouldn't be
  // prompted to rate the adventure; ratings should only come from published plays).
  useEffect(() => {
    if (adventure.isCompleted && !hasCheckedRating && !isSilentAdventure && !isTestMode) {
      setIsLoadingRating(true)
      ratingsApi.getUserRating(adventure.id)
        .then((rating) => {
          setUserRating(rating)
          if (rating) {
            setRatingScores({
              presenceScore: rating.presenceScore || 0,
              obscuraScore: rating.obscuraScore || 0,
              trailcraftScore: rating.trailcraftScore || 0,
              craftmarkScore: rating.craftmarkScore ?? null,
            })
            setRatingComment(rating.comment || '')
            setContainsSpoilers(rating.containsSpoilers || false)
          } else {
            // No rating yet - open the dialog automatically
            setRatingDialogOpen(true)
          }
          setHasCheckedRating(true)
        })
        .catch((err) => {
          console.error('Failed to fetch user rating:', err)
          setHasCheckedRating(true)
        })
        .finally(() => {
          setIsLoadingRating(false)
        })
    }
  }, [adventure.id, adventure.isCompleted, hasCheckedRating, isSilentAdventure, isTestMode])

  // Fetch creator profile for avatar and rank display
  useEffect(() => {
    const ownerId = adventure.owner?.entityId || adventure.createdBy
    if (ownerId) {
      profileApi.getUserProfile(ownerId)
        .then((response) => {
          // Handle wrapped response
          const profile = response && typeof response === 'object' && 'data' in response
            ? (response as any).data
            : response
          setCreatorProfile(profile)
        })
        .catch((err) => {
          console.error('Failed to fetch creator profile:', err)
        })
    }
  }, [adventure.owner?.entityId, adventure.createdBy])

  // Fetch all ratings for the dialog
  const fetchAllRatings = async (page: number = 1, sortBy = allRatingsSortBy, sortOrder = allRatingsSortOrder) => {
    setAllRatingsLoading(true)
    try {
      const response = await ratingsApi.getAdventureRatingsDetailed(adventure.id, {
        page,
        pageSize: 10,
        sortBy,
        sortOrder,
      })
      // Handle both paginated response and direct array response
      if (Array.isArray(response)) {
        // Direct array response
        setAllRatings(response)
        setAllRatingsPage(1)
        setAllRatingsTotalPages(1)
        setAllRatingsTotalCount(response.length)
      } else if (response && response.items) {
        // Paginated response
        setAllRatings(response.items || [])
        setAllRatingsPage(response.page || 1)
        setAllRatingsTotalPages(response.totalPages || 1)
        setAllRatingsTotalCount(response.totalCount || 0)
      } else {
        // Empty or unexpected response
        setAllRatings([])
        setAllRatingsPage(1)
        setAllRatingsTotalPages(1)
        setAllRatingsTotalCount(0)
      }
    } catch (err) {
      console.error('Failed to fetch ratings:', err)
      setAllRatings([])
    } finally {
      setAllRatingsLoading(false)
    }
  }

  // Open all ratings dialog
  const handleOpenAllRatings = () => {
    setAllRatingsDialogOpen(true)
    fetchAllRatings(1)
  }

  // Handle page change in all ratings dialog
  const handleAllRatingsPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchAllRatings(page, allRatingsSortBy, allRatingsSortOrder)
  }

  // Handle sort change in all ratings dialog
  const handleAllRatingsSortChange = (sortBy: 'CreatedAt' | 'PresenceScore' | 'ObscuraScore' | 'TrailcraftScore', sortOrder: 'asc' | 'desc') => {
    setAllRatingsSortBy(sortBy)
    setAllRatingsSortOrder(sortOrder)
    fetchAllRatings(1, sortBy, sortOrder)
  }

  // Handle rating submission
  const handleSubmitRating = async () => {
    // Validate required scores based on adventure type
    if (isGeocache) {
      if (!ratingScores.presenceScore) {
        setRatingError('Please rate the Presence category')
        return
      }
    } else if (isTrail) {
      if (!ratingScores.trailcraftScore) {
        setRatingError('Please rate the Trailcraft category')
        return
      }
    } else {
      if (!ratingScores.presenceScore || !ratingScores.obscuraScore || !ratingScores.trailcraftScore) {
        setRatingError('Please rate all required categories (Presence, Obscura, Trailcraft)')
        return
      }
    }

    setIsSubmittingRating(true)
    setRatingError(null)
    setRatingSuccess(false)

    try {
      // Build rating data based on adventure type
      // - Geocaches: only Presence
      // - Trails: only Trailcraft
      // - Others: all categories
      const ratingData = {
        presenceScore: isTrail ? 0 : ratingScores.presenceScore,
        obscuraScore: isGeocache || isTrail ? 0 : ratingScores.obscuraScore,
        trailcraftScore: isGeocache ? 0 : ratingScores.trailcraftScore,
        craftmarkScore: isGeocache || isTrail ? 0 : (ratingScores.craftmarkScore || 0),
        comment: ratingComment || undefined,
        containsSpoilers,
      }

      // The v2 backend returns the RatingDto directly (not wrapped). Upserts on POST,
      // so we can always call submitRating — but we still use PUT when we already have
      // an id so the id stays stable for optimistic UI.
      const saved = userRating
        ? await ratingsApi.updateRating(userRating.id, ratingData)
        : await ratingsApi.submitRating({
            targetType: RatingTargetType.Adventure,
            targetId: adventure.id,
            ...ratingData,
          })

      if (saved && saved.id) {
        setUserRating(saved)
        setRatingSuccess(true)
        // Close dialog and refresh adventure data
        setRatingDialogOpen(false)
        queryClient.invalidateQueries({ queryKey: ['adventure', adventure.id] })
      }
    } catch (err) {
      console.error('Failed to submit rating:', err)
      setRatingError('Failed to submit rating. Please try again.')
    } finally {
      setIsSubmittingRating(false)
    }
  }

  // Helper to update a single score
  const updateScore = (key: keyof typeof ratingScores, value: number | null) => {
    setRatingScores(prev => ({ ...prev, [key]: value }))
    setRatingSuccess(false)
  }

  // Check if all required scores are filled
  // - Geocaches only need Presence
  // - Trails only need Trailcraft
  // - Others need Presence, Obscura, and Trailcraft
  const hasRequiredScores = isGeocache
    ? ratingScores.presenceScore > 0
    : isTrail
      ? ratingScores.trailcraftScore > 0
      : ratingScores.presenceScore > 0 && ratingScores.obscuraScore > 0 && ratingScores.trailcraftScore > 0

  // State for rating info dialog
  const [ratingInfoOpen, setRatingInfoOpen] = useState<string | null>(null)

  // Handle continue adventure (when there's an active session)
  const handleContinueAdventure = () => {
    navigate(`/adventures/${adventure.id}`, { state: { sessionId: activeSession?.id } })
  }

  // Get session status label
  const getSessionStatusLabel = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.NotStarted:
        return 'Not Started'
      case SessionStatus.InProgress:
        return 'In Progress'
      case SessionStatus.Paused:
        return 'Paused'
      case SessionStatus.Completed:
        return 'Completed'
      case SessionStatus.Failed:
        return 'Failed'
      case SessionStatus.Abandoned:
        return 'Abandoned'
      default:
        return 'Unknown'
    }
  }

  // Get session status color for Chip
  const getSessionStatusColor = (status: SessionStatus): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case SessionStatus.NotStarted:
        return 'default'
      case SessionStatus.InProgress:
        return 'primary'
      case SessionStatus.Paused:
        return 'warning'
      case SessionStatus.Completed:
        return 'success'
      case SessionStatus.Failed:
        return 'error'
      case SessionStatus.Abandoned:
        return 'default'
      default:
        return 'default'
    }
  }

  // Format duration string (e.g., "00:05:30.1234567" -> "00:05:30")
  const formatDuration = (duration: string | null | undefined): string => {
    if (!duration) return '--:--'
    // Remove fractional seconds if present
    const parts = duration.split('.')
    return parts[0]
  }

  // Format UTC date to local date string
  const formatCompletionDate = (dateStr: string | null): string => {
    if (!dateStr) return '-'
    const date = new Date(dateStr + 'Z') // Append Z to ensure UTC parsing
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Format UTC date to local time string (for tooltip)
  const formatStartTime = (dateStr: string): string => {
    const date = new Date(dateStr + 'Z') // Append Z to ensure UTC parsing
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  // Determine if user can replay this adventure
  const canReplay = (): boolean => {
    // Testers / maintainers / owners always get to replay — they're exercising the
    // workflow during development, and the "cannot be repeated" restriction only
    // applies to normal players.
    if (canTest) return true

    // If adventure is repeatable, always allow replay
    if (adventure.isRepeatable) return true

    // If not repeatable, check if there are any non-test completed sessions
    const sessions = adventure.sessions || []
    const hasNonTestCompletedSession = sessions.some(
      (s) => s.status === SessionStatus.Completed && !s.isTestSession
    )

    // Allow replay only if all completed sessions are test sessions
    return !hasNonTestCompletedSession
  }

  const handlePlaySoloClick = () => {
    if (adventure.hasModerationNodes) {
      setPendingPlayAction('solo')
      setModerationWarningOpen(true)
    } else {
      setStartConfirmDialogOpen(true)
    }
  }

  const handleConfirmStart = async () => {
    setStartConfirmDialogOpen(false)
    try {
      const response = await soloJoinMutation.mutateAsync({ id: adventure.id })
      if (response.sessionId) {
        // Navigate to player view with session ID in state
        navigate(`/adventures/${adventure.id}`, {
          state: { sessionId: response.sessionId },
          replace: true,
        })
      } else if (!response.success) {
        // Only show error message if the request failed
        alert(response.message || 'Failed to join adventure')
      } else {
        // Success but no sessionId - this shouldn't happen but handle it
        alert('Session was created but could not be started. Please try again.')
      }
    } catch (err) {
      alert('Failed to join adventure')
    }
  }

  const handlePlayWithGroup = () => {
    if (adventure.hasModerationNodes) {
      setPendingPlayAction('group')
      setModerationWarningOpen(true)
    } else {
      setCreateGroupDialogOpen(true)
    }
  }

  const handleModerationWarningContinue = () => {
    setModerationWarningOpen(false)
    if (pendingPlayAction === 'solo') {
      handleConfirmStart()
    } else if (pendingPlayAction === 'group') {
      setCreateGroupDialogOpen(true)
    }
    setPendingPlayAction(null)
  }

  // Geocache-specific handlers
  const handleScanQRCode = () => {
    // Navigate to QR scanner page with adventure context
    navigate(`/adventures/${adventure.id}/scan`)
  }

  const handleEnterCode = () => {
    setEnteredCode('')
    setCodeError(null)
    setCodeEntryDialogOpen(true)
  }

  const handleCodeSubmit = async () => {
    if (!enteredCode.trim()) {
      setCodeError('Please enter a code')
      return
    }

    // TODO: Call API to validate code and complete geocache
    // For now, just navigate to player with the code
    try {
      const response = await soloJoinMutation.mutateAsync({
        id: adventure.id,
        // code: enteredCode.trim()  // Will need to add this to the API
      })
      if (response.sessionId) {
        setCodeEntryDialogOpen(false)
        navigate(`/adventures/${adventure.id}`, {
          state: { sessionId: response.sessionId, code: enteredCode.trim() },
          replace: true,
        })
      } else if (!response.success) {
        setCodeError(response.message || 'Invalid code')
      }
    } catch (err) {
      setCodeError('Failed to validate code. Please try again.')
    }
  }

  const handleGroupCreated = (response: AdventureJoinResponse) => {
    setCreateGroupDialogOpen(false)
    // Navigate to player if session created, otherwise to group
    if (response.sessionId) {
      navigate(`/adventures/${adventure.id}`, {
        state: { sessionId: response.sessionId },
        replace: true,
      })
    } else if (response.groupId) {
      navigate(`/groups/${response.groupId}`)
    } else {
      window.location.reload()
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this adventure?')) return
    try {
      await deleteMutation.mutateAsync(adventure.id)
      navigate('/adventures')
    } catch (err) {
      alert('Failed to delete adventure')
    }
  }

  const handleContactCreator = async () => {
    if (!contactMessage.trim()) {
      setContactError('Please enter a message')
      return
    }

    setIsSubmittingContact(true)
    setContactError(null)

    try {
      await createTicket({
        type: contactTicketType,
        priority: contactPriority,
        title: `${TicketTypeLabels[contactTicketType]}: ${adventure.title}`,
        description: contactMessage,
        targetType: TargetType.Adventure,
        targetId: adventure.id,
      })

      setContactSuccess(true)
      setContactMessage('')
      setTimeout(() => {
        setContactDialogOpen(false)
        setContactSuccess(false)
      }, 2000)
    } catch (err) {
      console.error('Failed to submit ticket:', err)
      setContactError('Failed to send message. Please try again.')
    } finally {
      setIsSubmittingContact(false)
    }
  }

  // Handle GPX export
  const handleExportGpx = async () => {
    setIsExportingGpx(true)
    setGpxExportError(null)

    try {
      // Fetch export data from API
      const exportData = await adventuresApi.getRoutesForExport(adventure.id)

      if (!exportData.exportEnabled) {
        setGpxExportError('GPX export is not enabled for this adventure')
        return
      }

      if (exportData.routes.length === 0) {
        setGpxExportError('No routes available for export')
        return
      }

      // Convert API data to GPX export options
      const gpxOptions: GpxExportOptions = {
        name: exportData.adventureName,
        description: exportData.adventureDescription,
        author: exportData.creatorName,
        routes: exportData.routes.map(route => ({
          id: route.id,
          label: route.label,
          travelMode: route.travelMode as RouteTravelMode,
          routePoints: route.routePoints,
          distanceMeters: route.distanceMeters,
          durationSeconds: route.durationSeconds,
        })),
        waypoints: exportData.waypoints?.map(wp => ({
          id: wp.id,
          name: wp.name,
          description: wp.description,
          latitude: wp.latitude,
          longitude: wp.longitude,
          type: wp.type,
        })),
      }

      // Download the GPX file
      downloadGpx(gpxOptions, `${adventure.title.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`)
    } catch (err) {
      console.error('Failed to export GPX:', err)
      setGpxExportError('Failed to export GPX. Please try again.')
    } finally {
      setIsExportingGpx(false)
    }
  }

  const isOwner = user?.id === (adventure.owner?.entityId || adventure.createdBy)

  // Owner management buttons rendered below the play/test actions
  const renderOwnerButtons = () => {
    if (!isOwner) return null
    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          size="small"
          startIcon={<Edit />}
          onClick={() => navigate(`/adventures/${adventure.id}/edit`)}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          size="small"
          startIcon={<Delete />}
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          Delete
        </Button>
      </Box>
    )
  }

  // Action buttons - extracted to render separately on mobile
  const renderActionButtons = () => {

    // If adventure is already completed, show completion status and session history
    if (adventure.isCompleted) {
      const sessions = adventure.sessions || []
      const allowReplay = canReplay()

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ textAlign: 'left' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Adventure Completed!
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {adventure.isRepeatable
                ? 'You can play this adventure again anytime.'
                : 'This adventure cannot be repeated.'}
            </Typography>
          </Alert>

          {/* Play Again Buttons */}
          {allowReplay && (
            <>
              <Button
                variant={isTestMode ? 'contained' : 'outlined'}
                fullWidth
                startIcon={isTestMode ? <ScienceIcon /> : <ReplayIcon />}
                onClick={handlePlaySoloClick}
                disabled={soloJoinMutation.isPending}
                color={isTestMode ? 'warning' : 'primary'}
              >
                {isTestMode ? 'Test Again (Solo)' : 'Play Again (Solo)'}
              </Button>
              {adventure.groupPlayStatus >= 1 && (
                <Badge
                  badgeContent={adventure.availableGroupSessionCount || 0}
                  color="primary"
                  invisible={!adventure.availableGroupSessionCount}
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={adventure.groupPlayStatus === 2 ? <Lock /> : <GroupIcon />}
                    onClick={adventure.groupPlayStatus === 2 ? () => setSubscriptionRequiredOpen(true) : handlePlayWithGroup}
                    disabled={soloJoinMutation.isPending}
                    color="primary"
                  >
                    Group Play
                  </Button>
                </Badge>
              )}
            </>
          )}

          {/* Session History - hidden on small screens where leaderboard "My Sessions" is available */}
          {sessions.length > 0 && (
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Your Attempts
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      {!isGeocache && <TableCell>Duration</TableCell>}
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>
                          {formatCompletionDate(session.completedAt || session.startedAt)}
                        </TableCell>
                        {!isGeocache && (
                          <TableCell>
                            <Tooltip
                              title={`Started: ${formatStartTime(session.startedAt)}`}
                              arrow
                            >
                              <Box
                                component="span"
                                sx={{
                                  cursor: 'help',
                                  borderBottom: '1px dotted',
                                  borderColor: 'text.secondary',
                                }}
                              >
                                {formatDuration(session.duration)}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={getSessionStatusLabel(session.status)}
                              color={getSessionStatusColor(session.status)}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                            {session.isTestSession && (
                              <Tooltip title="Test Session" arrow>
                                <ScienceIcon
                                  sx={{ fontSize: 16, color: 'info.main' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* GPX Export Button - for trail adventures */}
          {isTrail && (
            <Box>
              <Button
                variant="outlined"
                fullWidth
                startIcon={isExportingGpx ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExportGpx}
                disabled={isExportingGpx}
                color="secondary"
              >
                {isExportingGpx ? 'Exporting...' : 'Export Route (GPX)'}
              </Button>
              {gpxExportError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {gpxExportError}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )
    }

    // If checking for active session, show loading
    if (checkingSession) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    // If there's an active session, show Continue button
    // For group sessions, only show if the session is actually in progress
    if (activeSession && (!activeSession.isGroup || activeSession.status === SessionStatus.InProgress)) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={handleContinueAdventure}
            color="success"
            sx={{ py: 1.5 }}
          >
            Continue Adventure
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            Session: {getSessionStatusLabel(activeSession.status)}
            {activeSession.isGroup && ' • Group'}
          </Typography>
        </Box>
      )
    }

    // Check if user can join this adventure
    const meetsJoinCriteria = adventure.meetsJoinCriteria !== false
    const meetsDisplayCriteria = adventure.meetsDisplayCriteria !== false
    const hasRequiredSubscription = !adventure.requiresSubscription || user?.isPaidSubscription
    const canJoin = meetsJoinCriteria && meetsDisplayCriteria && hasRequiredSubscription

    if (!canJoin) {
      // Show why the user can't join
      const reasons: string[] = []
      if (!meetsJoinCriteria && adventure.joinCriteriaFailureReasons?.length) {
        reasons.push(...adventure.joinCriteriaFailureReasons)
      }
      if (!meetsDisplayCriteria) {
        reasons.push('This adventure is not available to you')
      }
      if (adventure.requiresSubscription && !user?.isPaidSubscription) {
        reasons.push('This adventure requires a paid subscription')
      }

      return (
        <Alert
          severity="warning"
          icon={<Lock />}
          sx={{ textAlign: 'left' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Currently Unavailable
          </Typography>
          {reasons.map((reason, idx) => (
            <Typography key={idx} variant="body2" sx={{ fontSize: '0.8rem' }}>
              {reason}
            </Typography>
          ))}
        </Alert>
      )
    }

    // For categories with QR code as the only join method (e.g., geocaches), show Scan Code button
    if (isQrCodeOnlyJoin || isGeocache) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<QrCodeScannerIcon />}
            onClick={handleScanQRCode}
            disabled={soloJoinMutation.isPending}
            color="primary"
          >
            Scan Code
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<KeyboardIcon />}
            onClick={handleEnterCode}
            disabled={soloJoinMutation.isPending}
            color="primary"
          >
            Enter Code
          </Button>
        </Box>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={isTestMode ? <ScienceIcon /> : <PersonIcon />}
          onClick={handlePlaySoloClick}
          disabled={soloJoinMutation.isPending}
          color={isTestMode ? 'warning' : 'primary'}
          sx={isTestMode ? { fontWeight: 700, letterSpacing: 0.3 } : undefined}
        >
          {isTestMode ? 'Test Solo' : 'Play Solo'}
        </Button>
        {adventure.groupPlayStatus >= 1 && (
          <Badge
            badgeContent={adventure.availableGroupSessionCount || 0}
            color="primary"
            invisible={!adventure.availableGroupSessionCount}
            sx={{ width: '100%' }}
          >
            <Button
              variant="outlined"
              fullWidth
              startIcon={adventure.groupPlayStatus === 2 ? <Lock /> : <GroupIcon />}
              onClick={adventure.groupPlayStatus === 2 ? () => setSubscriptionRequiredOpen(true) : handlePlayWithGroup}
              disabled={soloJoinMutation.isPending}
              color="primary"
            >
              Group Play
            </Button>
          </Badge>
        )}
      </Box>
    )
  }

  // Details sidebar content - rendered in different positions based on screen size
  const renderDetailsSidebar = () => (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            Details
          </Typography>
          {user && !isOwner && (
            <Tooltip title="Contact adventure creator">
              <IconButton
                size="small"
                onClick={() => setContactDialogOpen(true)}
                color="primary"
              >
                <SupportAgentIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Ratings Section - at the top (hidden for silent adventures) */}
        {!isSilentAdventure && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <ToggleButtonGroup
                  value={ratingViewMode}
                  exclusive
                  onChange={(_, value) => value && setRatingViewMode(value)}
                  size="small"
                  sx={{ height: 28 }}
                >
                  <ToggleButton value="average" sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>
                    <PeopleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Community
                  </ToggleButton>
                  <ToggleButton value="user" sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>
                    <StarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Yours
                  </ToggleButton>
                </ToggleButtonGroup>
                {ratingViewMode === 'average' && adventure.rating && adventure.rating.totalRatings > 0 && (
                  <Tooltip title="View all ratings">
                    <IconButton size="small" onClick={handleOpenAllRatings}>
                      <PeopleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                {ratingViewMode === 'average' ? (
                  // Community Ratings
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Difficulty - layered display showing creator (purple) and community (gold) */}
                    {(() => {
                      const creatorDifficulty = adventure.difficulty || 0
                      const communityDifficulty = adventure.rating?.averageDifficultyScore ?? null
                      const hasCommunityRating = communityDifficulty !== null && (adventure.rating?.difficultyRatingCount ?? 0) > 0

                      // Determine which is lower to render first
                      const lowerValue = hasCommunityRating
                        ? Math.min(creatorDifficulty, communityDifficulty)
                        : creatorDifficulty
                      const higherValue = hasCommunityRating
                        ? Math.max(creatorDifficulty, communityDifficulty)
                        : creatorDifficulty
                      const creatorIsLower = creatorDifficulty <= (communityDifficulty ?? creatorDifficulty)

                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                          <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                            ⛰️ Difficulty
                          </Typography>
                          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            {/* Empty stars background - always visible */}
                            <Rating value={0} readOnly size="small" max={5} />
                            {/* Base layer - lower rating */}
                            <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
                              <Rating
                                value={lowerValue}
                                readOnly
                                size="small"
                                precision={0.5}
                                sx={{
                                  '& .MuiRating-iconFilled': {
                                    color: creatorIsLower ? '#9c27b0' : '#ffc107', // Purple if creator is lower, gold if community
                                  },
                                  '& .MuiRating-iconEmpty': {
                                    color: 'transparent',
                                  },
                                }}
                              />
                            </Box>
                            {/* Top layer - higher rating extending beyond lower */}
                            {hasCommunityRating && higherValue > lowerValue && (
                              <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
                                <Rating
                                  value={higherValue}
                                  readOnly
                                  size="small"
                                  precision={0.5}
                                  sx={{
                                    '& .MuiRating-iconFilled': {
                                      color: creatorIsLower ? '#ffc107' : '#9c27b0', // Gold if community is higher, purple if creator
                                      clipPath: `inset(0 0 0 ${(lowerValue / 5) * 100}%)`,
                                    },
                                    '& .MuiRating-iconEmpty': {
                                      color: 'transparent',
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )
                    })()}
                    <Divider sx={{ my: 0.5 }} />
                    {/* Presence - hide for trails */}
                    {!isTrail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                          {RATING_CATEGORIES.presence.icon} Presence
                        </Typography>
                        <Rating value={adventure.rating?.averagePresenceScore ?? 0} readOnly size="small" precision={0.5} sx={{ flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: 'right', flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
                          {adventure.rating && adventure.rating.totalRatings > 0 ? adventure.rating.averagePresenceScore.toFixed(1) : ''}
                        </Typography>
                      </Box>
                    )}
                    {/* Obscura - hide for geocaches and trails */}
                    {!isGeocache && !isTrail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                          {RATING_CATEGORIES.obscura.icon} Obscura
                        </Typography>
                        <Rating value={adventure.rating?.averageObscuraScore ?? 0} readOnly size="small" precision={0.5} sx={{ flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: 'right', flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
                          {adventure.rating && adventure.rating.totalRatings > 0 ? adventure.rating.averageObscuraScore.toFixed(1) : ''}
                        </Typography>
                      </Box>
                    )}
                    {/* Trailcraft - hide for geocaches */}
                    {!isGeocache && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                          {RATING_CATEGORIES.trailcraft.icon} Trailcraft
                        </Typography>
                        <Rating value={adventure.rating?.averageTrailcraftScore ?? 0} readOnly size="small" precision={0.5} sx={{ flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: 'right', flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
                          {adventure.rating && adventure.rating.totalRatings > 0 ? adventure.rating.averageTrailcraftScore.toFixed(1) : ''}
                        </Typography>
                      </Box>
                    )}
                    {/* Craftmark - hide for geocaches and trails, only show if there's a score */}
                    {!isGeocache && !isTrail && adventure.rating?.averageCraftmarkScore && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                          {RATING_CATEGORIES.craftmark.icon} Craftmark
                        </Typography>
                        <Rating value={adventure.rating.averageCraftmarkScore} readOnly size="small" precision={0.5} sx={{ flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: 'right', flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
                          {adventure.rating.averageCraftmarkScore.toFixed(1)}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {adventure.rating && adventure.rating.totalRatings > 0 ? (
                        <>
                          <Typography variant="caption" fontWeight={600}>
                            Overall: {adventure.rating.overallAverageScore.toFixed(1)}/5
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {adventure.rating.totalRatings} rating{adventure.rating.totalRatings !== 1 ? 's' : ''}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No ratings yet
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  // User Rating
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {isLoadingRating ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : userRating ? (
                      <>
                        {/* Presence - hide for trails */}
                        {!isTrail && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 70 }}>
                              {RATING_CATEGORIES.presence.icon} Presence
                            </Typography>
                            <Rating value={userRating.presenceScore} readOnly size="small" />
                          </Box>
                        )}
                        {/* Obscura - hide for geocaches and trails */}
                        {!isGeocache && !isTrail && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 70 }}>
                              {RATING_CATEGORIES.obscura.icon} Obscura
                            </Typography>
                            <Rating value={userRating.obscuraScore} readOnly size="small" />
                          </Box>
                        )}
                        {/* Trailcraft - hide for geocaches */}
                        {!isGeocache && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 70 }}>
                              {RATING_CATEGORIES.trailcraft.icon} Trailcraft
                            </Typography>
                            <Rating value={userRating.trailcraftScore} readOnly size="small" />
                          </Box>
                        )}
                        {/* Craftmark - hide for geocaches and trails, only show if there's a score */}
                        {!isGeocache && !isTrail && userRating.craftmarkScore && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 70 }}>
                              {RATING_CATEGORIES.craftmark.icon} Craftmark
                            </Typography>
                            <Rating value={userRating.craftmarkScore} readOnly size="small" />
                          </Box>
                        )}
                        {userRating.comment && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.8rem' }}>
                            "{userRating.comment}"
                          </Typography>
                        )}
                        {!isTestMode && (
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => setRatingDialogOpen(true)}
                            sx={{ mt: 1 }}
                          >
                            Edit Rating
                          </Button>
                        )}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {isTestMode
                            ? 'Ratings are disabled for test sessions'
                            : adventure.isCompleted
                              ? "You haven't rated this adventure yet"
                              : 'Complete the adventure to rate it'}
                        </Typography>
                        {adventure.isCompleted && !isTestMode && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<StarIcon />}
                            onClick={() => setRatingDialogOpen(true)}
                            color="warning"
                          >
                            Rate Now
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
        )}

        {!isSilentAdventure && <Divider sx={{ my: 1.5 }} />}

        {/* Created date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CalendarToday color="action" fontSize="small" />
          <Typography variant="body2">
            Created {new Date(adventure.createdDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).replace(',', '')}
          </Typography>
        </Box>

        {/* Location */}
        {adventure.location && adventure.location.latitude && adventure.location.longitude && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
              <LocationOn color="action" fontSize="small" />
              <Typography variant="body2">
                {adventure.location.address || `${adventure.location.latitude.toFixed(4)}, ${adventure.location.longitude.toFixed(4)}`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<Navigation />}
              sx={{ mb: 1 }}
              onClick={() => navigateTo(adventure.location!, userLocation)}
            >
              Get Directions
            </Button>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Brought to you by */}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Brought to you by
        </Typography>
        <Tooltip
          title={
            creatorProfile?.rankName ? (
              <Box sx={{ textAlign: 'center', py: 0.5 }}>
                {creatorProfile.rankBadge && (
                  <Box
                    component="img"
                    src={creatorProfile.rankBadge}
                    alt={creatorProfile.rankName}
                    sx={{ width: 32, height: 32, mb: 0.5 }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {creatorProfile.rankName}
                </Typography>
                {creatorProfile.pointsToNextRank > 0 && creatorProfile.nextRankName && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {creatorProfile.pointsToNextRank} pts to {creatorProfile.nextRankName}
                  </Typography>
                )}
              </Box>
            ) : 'View profile'
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              p: 1,
              mx: -1,
              borderRadius: 1,
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => navigate(`/profile/${adventure.owner?.entityId || adventure.createdBy}`)}
          >
            <Avatar
              src={adventure.owner?.entityAvatarUrl || creatorProfile?.avatar || undefined}
              alt={adventure.owner?.entityName || adventure.createdByName}
              sx={{ width: 32, height: 32 }}
            >
              {(adventure.owner?.entityName || adventure.createdByName)?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {adventure.owner?.entityName || adventure.createdByName}
              </Typography>
              {creatorProfile?.rankName && (
                <Typography variant="caption" color="text.secondary">
                  {creatorProfile.rankName}
                </Typography>
              )}
            </Box>
          </Box>
        </Tooltip>

        {/* Action buttons - only shown when sidebar is visible (960px+) */}
        {!showDetailsAsTab && (
          <>
            <Divider sx={{ my: 2 }} />
            {renderActionButtons()}
            {renderOwnerButtons()}
          </>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box sx={{ minHeight: '100%' }}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Mobile: Image at top, full width */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'relative' }}>
          <CardMedia
            component="img"
            sx={{ height: 200, objectFit: 'cover' }}
            image={adventure.coverImageUrl || adventure.imageUrl || `https://picsum.photos/seed/${adventure.id}/1200/400`}
            alt={adventure.title}
          />
          {/* Floating chips over image. When a Testing workflow version is available to
              this user (hasTestVersion) we surface a Testing pill even if the adventure's
              top-level status is still Draft, so the tester sees what they're about to run. */}
          {(adventure.status === 1 || adventure.status === 2 || isTestMode) && (
            <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
              <Chip
                icon={isTestMode ? <ScienceIcon /> : undefined}
                label={isTestMode ? 'Testing' : getAdventureStatusLabel(adventure.status)}
                color={isTestMode ? 'warning' : getAdventureStatusColor(adventure.status)}
                size="small"
                sx={{ fontWeight: 600, boxShadow: 1 }}
              />
            </Box>
          )}
        </Box>

        {/* Desktop: Two-column layout - content on left, sidebar on right */}
        <Box sx={{ display: showDetailsAsTab ? 'block' : 'flex', alignItems: 'stretch', flex: 1 }}>
          {/* Left column: Image + all content */}
          <Box sx={{ flex: showDetailsAsTab ? undefined : '1 1 66.666%', maxWidth: showDetailsAsTab ? undefined : '66.666%', display: 'flex', flexDirection: 'column' }}>
            {/* Desktop image */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
              <CardMedia
                component="img"
                sx={{ height: 300, objectFit: 'cover' }}
                image={adventure.coverImageUrl || adventure.imageUrl || `https://picsum.photos/seed/${adventure.id}/1200/400`}
                alt={adventure.title}
              />
              {/* Floating chips over image (desktop) */}
              {(adventure.status === 1 || adventure.status === 2 || isTestMode) && (
                <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1 }}>
                  <Chip
                    icon={isTestMode ? <ScienceIcon /> : undefined}
                    label={isTestMode ? 'Testing' : getAdventureStatusLabel(adventure.status)}
                    color={isTestMode ? 'warning' : getAdventureStatusColor(adventure.status)}
                    sx={{ fontWeight: 600, boxShadow: 2 }}
                  />
                </Box>
              )}
            </Box>

            <CardContent sx={{ flex: 1 }}>
              {/* Title */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" gutterBottom>
                  {adventure.title}
                </Typography>
              </Box>

              {/* Testing mode alert — only shown when the current user has tester-or-higher
                  access to an adventure with a Testing workflow version. Tells them what a
                  normal user would see and why. Not shown to regular players. */}
              {isTestMode && (
                <Alert
                  severity="warning"
                  icon={<ScienceIcon />}
                  sx={{ mb: 2, textAlign: 'left' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    You're viewing this as a {isTester ? 'Tester' : isMaintainer ? 'Maintainer' : isAdventureOwner ? 'Owner' : 'Moderator'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    This adventure is running its <strong>Testing</strong> workflow version{adventure.testingWorkflowVersion ? ` (v${adventure.testingWorkflowVersion})` : ''}
                    {adventure.publishedWorkflowVersion
                      ? `, while normal players would see v${adventure.publishedWorkflowVersion}.`
                      : '. Normal players cannot see or join this adventure yet — it has no Published version.'}
                    {' '}Ratings, achievements, and leaderboard entries from test sessions don't count.
                  </Typography>
                  {/* Unmet display/join criteria — the backend stubs meetsDisplayCriteria /
                      meetsJoinCriteria to true today, so this branch is dormant until the
                      criteria evaluation engine lands. Kept in place so it activates for
                      testers automatically once the backend returns real values. */}
                  {(adventure.meetsDisplayCriteria === false || adventure.meetsJoinCriteria === false) && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'warning.dark' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        A regular user would be blocked:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.8rem' }}>
                        {adventure.meetsDisplayCriteria === false && (
                          <li>Does not meet display criteria (wouldn't appear in discovery)</li>
                        )}
                        {adventure.meetsJoinCriteria === false && (
                          <li>Does not meet join criteria (would be unable to start)</li>
                        )}
                        {adventure.joinCriteriaFailureReasons?.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}

              {/* Action buttons shown when sidebar is hidden (below 960px) */}
              {showDetailsAsTab && (
                <Box sx={{ mb: 2 }}>
                  {renderActionButtons()}
                  {renderOwnerButtons()}
                </Box>
              )}

          {/* Content Tab Selector */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={activeContentTab}
              exclusive
              onChange={(_, value) => value && setActiveContentTab(value)}
              size="small"
              sx={{
                width: '100%',
                '& .MuiToggleButton-root': {
                  flex: 1,
                  py: 1,
                },
              }}
            >
              <ToggleButton value="description">
                <DescriptionIcon sx={{ fontSize: 18, mr: 0.5 }} />
                About
              </ToggleButton>
              {!isSilentAdventure && (
                <ToggleButton value="discussion">
                  <ForumIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  Discussion
                </ToggleButton>
              )}
              {!isSilentAdventure && categorySettings?.statisticTypes !== StatisticTypes.None && (
                <ToggleButton value="leaderboard">
                  <LeaderboardIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  {categorySettings?.statisticTypes === StatisticTypes.CompletionList ? 'Completions' : 'Leaderboard'}
                </ToggleButton>
              )}
              {/* Details tab - shown below 960px (when sidebar is hidden) */}
              {showDetailsAsTab && (
                <ToggleButton value="details">
                  <InfoIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  Details
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>

          {/* Tab Content */}
          <Box sx={{ width: '100%' }}>
            {/* Description Tab */}
            {activeContentTab === 'description' && (
              <>
                <Typography
                  variant="body1"
                  component="div"
                  paragraph
                  sx={{
                    '& p': { marginBottom: 1 },
                    '& p:last-child': { marginBottom: 0 },
                  }}
                  dangerouslySetInnerHTML={{ __html: adventure.description }}
                />

                <Divider sx={{ my: 3 }} />

                {adventure.tags && adventure.tags.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      {(() => {
                        // Extract tag IDs - handle both string tags and object tags
                        const tagIds = adventure.tags.map((tag: any) =>
                          typeof tag === 'string' ? tag : tag.id
                        )
                        // Look up full tag data from cache
                        const tagObjects = getTags(tagIds)
                        return tagObjects.map((tag) => (
                          <Chip
                            key={tag.id}
                            label={tag.displayName || tag.name}
                            icon={tag.icon ? <span style={{ marginLeft: 8 }}>{tag.icon}</span> : undefined}
                            variant="outlined"
                            sx={tag.color ? {
                              borderColor: tag.color,
                              color: tag.color,
                              '& .MuiChip-icon': { color: tag.color },
                            } : undefined}
                          />
                        ))
                      })()}
                    </Box>
                  </>
                )}

                {adventure.location && adventure.location.latitude && adventure.location.longitude && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Location
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Navigation />}
                        onClick={() => navigateTo(adventure.location!, userLocation)}
                      >
                        Navigate
                      </Button>
                    </Box>
                    <Box sx={{ position: 'relative', height: '300px', borderRadius: 1, overflow: 'hidden' }}>
                      <LazyMap
                        key={`adventure-map-${adventure.id}`}
                        center={[adventure.location.latitude, adventure.location.longitude]}
                        zoom={15}
                        height="300px"
                      >
                        {/* User location marker */}
                        {userLocation && userLocation.source === 'gps' && (
                          <>
                            {/* Accuracy circle */}
                            {userLocation.accuracy && (
                              <Circle
                                center={[userLocation.latitude, userLocation.longitude]}
                                radius={userLocation.accuracy}
                                pathOptions={{
                                  color: '#2196F3',
                                  fillColor: '#2196F3',
                                  fillOpacity: 0.1,
                                  weight: 1,
                                }}
                              />
                            )}

                            {/* User location marker */}
                            <Marker
                              position={[userLocation.latitude, userLocation.longitude]}
                              icon={userLocationIcon}
                            >
                              <Popup>
                                <Typography variant="subtitle2">Your Location</Typography>
                                {userLocation.accuracy && (
                                  <Typography variant="caption">
                                    Accuracy: ±{Math.round(userLocation.accuracy)}m
                                  </Typography>
                                )}
                              </Popup>
                            </Marker>
                          </>
                        )}

                        {/* Adventure location marker */}
                        <Marker position={[adventure.location.latitude, adventure.location.longitude]}>
                          <Popup>
                            <Typography variant="subtitle2">{adventure.title}</Typography>
                            {adventure.location.address && (
                              <Typography variant="caption">{adventure.location.address}</Typography>
                            )}
                          </Popup>
                        </Marker>
                      </LazyMap>
                    </Box>
                  </>
                )}
              </>
            )}

            {/* Discussion Tab (hidden for silent adventures) */}
            {activeContentTab === 'discussion' && !isSilentAdventure && (
              <AdventureDiscussions
                adventureId={adventure.id}
                adventureTitle={adventure.title}
                canPost={!!user}
                canReply={!!user}
              />
            )}

            {/* Leaderboard/Completions Tab (hidden for silent adventures) */}
            {activeContentTab === 'leaderboard' && !isSilentAdventure && categorySettings?.statisticTypes === StatisticTypes.Leaderboard && (
              <AdventureLeaderboard adventureId={adventure.id} sessions={adventure.sessions} />
            )}
            {activeContentTab === 'leaderboard' && !isSilentAdventure && categorySettings?.statisticTypes === StatisticTypes.CompletionList && (
              <RecentCheckins adventureId={adventure.id} />
            )}

            {/* Details Tab (shown below 960px when sidebar is hidden) */}
            {activeContentTab === 'details' && showDetailsAsTab && (
              <Box>
                {renderDetailsSidebar()}
              </Box>
            )}
          </Box>

            </CardContent>
          </Box>

          {/* Right column: Sidebar (shown at 960px and above) */}
          {!showDetailsAsTab && (
            <Box sx={{
              flex: '0 0 33.333%',
              maxWidth: '33.333%',
              p: 2,
              borderLeft: 1,
              borderColor: 'divider',
            }}>
              {renderDetailsSidebar()}
            </Box>
          )}
        </Box>
      </Card>

      {/* Group Play Dialog */}
      <GroupPlayDialog
        open={createGroupDialogOpen}
        onClose={() => setCreateGroupDialogOpen(false)}
        adventure={adventure}
        onSuccess={handleGroupCreated}
      />

      {/* Contact Creator Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => {
          setContactDialogOpen(false)
          setContactMessage('')
          setContactTicketType(TicketType.Question)
          setContactPriority(TicketPriority.Medium)
          setContactError(null)
          setContactSuccess(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgentIcon color="primary" />
          Contact Adventure Creator
        </DialogTitle>
        <DialogContent>
          {contactSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Your message has been sent successfully!
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                Send a message to <strong>{adventure.owner?.entityName || adventure.createdByName}</strong> about this adventure.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={contactTicketType}
                    label="Type"
                    onChange={(e) => setContactTicketType(e.target.value as TicketType)}
                    disabled={isSubmittingContact}
                  >
                    <MenuItem value={TicketType.Question}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.Question} size="small" />
                        {TicketTypeLabels[TicketType.Question]}
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketType.Bug}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.Bug} size="small" />
                        {TicketTypeLabels[TicketType.Bug]}
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketType.FeatureRequest}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.FeatureRequest} size="small" />
                        {TicketTypeLabels[TicketType.FeatureRequest]}
                      </Box>
                    </MenuItem>
                    <MenuItem divider />
                    <MenuItem value={TicketType.LocationPrivateProperty}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.LocationPrivateProperty} size="small" />
                        {TicketTypeLabels[TicketType.LocationPrivateProperty]}
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketType.LocationMissing}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.LocationMissing} size="small" />
                        {TicketTypeLabels[TicketType.LocationMissing]}
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketType.LocationInaccessible}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.LocationInaccessible} size="small" />
                        {TicketTypeLabels[TicketType.LocationInaccessible]}
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketType.LocationSafety}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.LocationSafety} size="small" />
                        {TicketTypeLabels[TicketType.LocationSafety]}
                      </Box>
                    </MenuItem>
                    <MenuItem divider />
                    <MenuItem value={TicketType.Other}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketTypeIcon type={TicketType.Other} size="small" />
                        {TicketTypeLabels[TicketType.Other]}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={contactPriority}
                    label="Priority"
                    onChange={(e) => setContactPriority(e.target.value as TicketPriority)}
                    disabled={isSubmittingContact}
                    MenuProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                    }}
                  >
                    <MenuItem value={TicketPriority.Low}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketPriorityIndicator priority={TicketPriority.Low} variant="icon" size="small" />
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketPriority.Medium}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketPriorityIndicator priority={TicketPriority.Medium} variant="icon" size="small" />
                      </Box>
                    </MenuItem>
                    <MenuItem value={TicketPriority.High}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketPriorityIndicator priority={TicketPriority.High} variant="icon" size="small" />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                autoFocus
                multiline
                rows={4}
                fullWidth
                label="Your message"
                placeholder="Describe your question or issue..."
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                disabled={isSubmittingContact}
                error={!!contactError}
                helperText={contactError}
              />
            </>
          )}
        </DialogContent>
        {!contactSuccess && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setContactDialogOpen(false)
                setContactMessage('')
                setContactTicketType(TicketType.Question)
                setContactPriority(TicketPriority.Medium)
                setContactError(null)
              }}
              disabled={isSubmittingContact}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleContactCreator}
              disabled={isSubmittingContact || !contactMessage.trim()}
              startIcon={isSubmittingContact ? <CircularProgress size={16} /> : undefined}
            >
              {isSubmittingContact ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Moderation Warning Dialog */}
      <Dialog
        open={moderationWarningOpen}
        onClose={() => { setModerationWarningOpen(false); setPendingPlayAction(null) }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettingsIcon color="warning" />
          Moderation Notice
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This adventure includes moderation checkpoints that may notify designated moderators of your activity and location during the adventure.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            By continuing, you acknowledge that moderators assigned to this adventure may be alerted to your presence at certain points along the route.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setModerationWarningOpen(false); setPendingPlayAction(null) }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleModerationWarningContinue}
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Adventure Confirmation Dialog */}
      <Dialog
        open={startConfirmDialogOpen}
        onClose={() => setStartConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon color="warning" />
          Ready to Begin?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The adventure timer will start the moment you click <strong>Start Adventure</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Make sure you're ready and at the starting location before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStartConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStart}
            disabled={soloJoinMutation.isPending}
            autoFocus
          >
            Start Adventure
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enter Code Dialog (for Geocaches) */}
      <Dialog
        open={codeEntryDialogOpen}
        onClose={() => setCodeEntryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyboardIcon color="primary" />
          Enter Cache Code
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the code found at the geocache location to validate your find.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Cache Code"
            value={enteredCode}
            onChange={(e) => {
              setEnteredCode(e.target.value.toUpperCase())
              setCodeError(null)
            }}
            error={!!codeError}
            helperText={codeError}
            placeholder="Enter code..."
            inputProps={{
              style: { textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCodeSubmit()
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCodeEntryDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCodeSubmit}
            disabled={soloJoinMutation.isPending || !enteredCode.trim()}
          >
            {soloJoinMutation.isPending ? <CircularProgress size={20} /> : 'Validate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog
        open={ratingDialogOpen}
        onClose={() => setRatingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon color="warning" />
          {userRating ? 'Update Your Rating' : isGeocache ? 'Rate this Geocache' : isTrail ? 'Rate this Trail' : 'Rate this Adventure'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isGeocache
              ? 'Share your experience to help other geocachers! Rate each category based on your find.'
              : isTrail
                ? 'Share your experience to help other trail runners! Rate the trail design and navigation.'
                : 'Share your experience to help other adventurers! Rate each category based on your experience.'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Rating Categories Grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: isGeocache || isTrail ? '1fr' : '1fr 1fr' },
              gap: 2,
            }}>
              {/* Presence Score - hide for trails */}
              {!isTrail && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <span>{RATING_CATEGORIES.presence.icon}</span>
                    <Typography variant="subtitle2">
                      {RATING_CATEGORIES.presence.label}
                    </Typography>
                    <Typography component="span" color="error" sx={{ fontSize: '0.75rem' }}>*</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRatingInfoOpen('presence')}
                      sx={{ ml: 'auto', p: 0.25 }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Box>
                  <Tooltip
                    title={ratingScores.presenceScore > 0
                      ? RATING_CATEGORIES.presence.scoringGuide[ratingScores.presenceScore as keyof typeof RATING_CATEGORIES.presence.scoringGuide]
                      : 'Select a rating'}
                    arrow
                  >
                    <Box sx={{ display: 'inline-flex' }}>
                      <Rating
                        value={ratingScores.presenceScore}
                        onChange={(_, newValue) => updateScore('presenceScore', newValue || 0)}
                        size="medium"
                      />
                    </Box>
                  </Tooltip>
                </Paper>
              )}

              {/* Obscura Score - only for non-geocaches and non-trails */}
              {!isGeocache && !isTrail && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <span>{RATING_CATEGORIES.obscura.icon}</span>
                    <Typography variant="subtitle2">
                      {RATING_CATEGORIES.obscura.label}
                    </Typography>
                    <Typography component="span" color="error" sx={{ fontSize: '0.75rem' }}>*</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRatingInfoOpen('obscura')}
                      sx={{ ml: 'auto', p: 0.25 }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Box>
                  <Tooltip
                    title={ratingScores.obscuraScore > 0
                      ? RATING_CATEGORIES.obscura.scoringGuide[ratingScores.obscuraScore as keyof typeof RATING_CATEGORIES.obscura.scoringGuide]
                      : 'Select a rating'}
                    arrow
                  >
                    <Box sx={{ display: 'inline-flex' }}>
                      <Rating
                        value={ratingScores.obscuraScore}
                        onChange={(_, newValue) => updateScore('obscuraScore', newValue || 0)}
                        size="medium"
                      />
                    </Box>
                  </Tooltip>
                </Paper>
              )}

              {/* Trailcraft Score - for non-geocaches (required for trails) */}
              {!isGeocache && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <span>{RATING_CATEGORIES.trailcraft.icon}</span>
                    <Typography variant="subtitle2">
                      {RATING_CATEGORIES.trailcraft.label}
                    </Typography>
                    <Typography component="span" color="error" sx={{ fontSize: '0.75rem' }}>*</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRatingInfoOpen('trailcraft')}
                      sx={{ ml: 'auto', p: 0.25 }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Box>
                  <Tooltip
                    title={ratingScores.trailcraftScore > 0
                      ? RATING_CATEGORIES.trailcraft.scoringGuide[ratingScores.trailcraftScore as keyof typeof RATING_CATEGORIES.trailcraft.scoringGuide]
                      : 'Select a rating'}
                    arrow
                  >
                    <Box sx={{ display: 'inline-flex' }}>
                      <Rating
                        value={ratingScores.trailcraftScore}
                        onChange={(_, newValue) => updateScore('trailcraftScore', newValue || 0)}
                        size="medium"
                      />
                    </Box>
                  </Tooltip>
                </Paper>
              )}

              {/* Craftmark Score (Optional) - only for non-geocaches and non-trails */}
              {!isGeocache && !isTrail && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <span>{RATING_CATEGORIES.craftmark.icon}</span>
                    <Typography variant="subtitle2">
                      {RATING_CATEGORIES.craftmark.label}
                    </Typography>
                    <Typography component="span" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5 }}>
                      (optional)
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRatingInfoOpen('craftmark')}
                      sx={{ ml: 'auto', p: 0.25 }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Box>
                  <Tooltip
                    title={ratingScores.craftmarkScore && ratingScores.craftmarkScore > 0
                      ? RATING_CATEGORIES.craftmark.scoringGuide[ratingScores.craftmarkScore as keyof typeof RATING_CATEGORIES.craftmark.scoringGuide]
                      : 'Select a rating (optional)'}
                    arrow
                  >
                    <Box sx={{ display: 'inline-flex' }}>
                      <Rating
                        value={ratingScores.craftmarkScore || 0}
                        onChange={(_, newValue) => updateScore('craftmarkScore', newValue)}
                        size="medium"
                      />
                    </Box>
                  </Tooltip>
                </Paper>
              )}
            </Box>

            {/* Comment */}
            <TextField
              label="Comment (optional)"
              placeholder="Share your thoughts about this adventure..."
              value={ratingComment}
              onChange={(e) => {
                setRatingComment(e.target.value)
                setRatingSuccess(false)
              }}
              multiline
              rows={3}
              fullWidth
            />

            {/* Contains Spoilers checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={containsSpoilers}
                  onChange={(e) => setContainsSpoilers(e.target.checked)}
                />
              }
              label="My comment contains spoilers"
            />

            {ratingError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {ratingError}
              </Alert>
            )}

            {ratingSuccess && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1 }}>
                Rating submitted successfully!
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRatingDialogOpen(false)}>
            {userRating ? 'Cancel' : 'Maybe Later'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRating}
            disabled={isSubmittingRating || !hasRequiredScores}
            color="warning"
          >
            {isSubmittingRating ? (
              <CircularProgress size={20} color="inherit" />
            ) : userRating ? (
              'Update Rating'
            ) : (
              'Submit Rating'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Ratings Dialog */}
      <Dialog
        open={allRatingsDialogOpen}
        onClose={() => setAllRatingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RateReviewIcon color="primary" />
          All Ratings ({allRatingsTotalCount})
        </DialogTitle>
        <DialogContent dividers>
          {/* Sort Controls */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={allRatingsSortBy}
                label="Sort By"
                onChange={(e) => handleAllRatingsSortChange(e.target.value as typeof allRatingsSortBy, allRatingsSortOrder)}
              >
                <MenuItem value="CreatedAt">Date</MenuItem>
                <MenuItem value="PresenceScore">Presence</MenuItem>
                <MenuItem value="ObscuraScore">Obscura</MenuItem>
                <MenuItem value="TrailcraftScore">Trailcraft</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={allRatingsSortOrder}
                label="Order"
                onChange={(e) => handleAllRatingsSortChange(allRatingsSortBy, e.target.value as 'asc' | 'desc')}
              >
                <MenuItem value="desc">{allRatingsSortBy === 'CreatedAt' ? 'Newest First' : 'Highest First'}</MenuItem>
                <MenuItem value="asc">{allRatingsSortBy === 'CreatedAt' ? 'Oldest First' : 'Lowest First'}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {allRatingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !allRatings || allRatings.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No ratings yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {allRatings.map((rating) => (
                <Paper key={rating.id} variant="outlined" sx={{ p: 2 }}>
                  {/* User info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Avatar
                      src={rating.user?.avatarUrl || undefined}
                      sx={{ width: 32, height: 32 }}
                    >
                      {rating.user?.displayName?.[0] || rating.user?.username?.[0] || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">
                        {rating.user?.displayName || rating.user?.username || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(rating.createdAt + 'Z').toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Ratings grid */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                    mb: rating.comment ? 1.5 : 0,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ minWidth: 70 }}>
                        {RATING_CATEGORIES.presence.icon} Presence
                      </Typography>
                      <Rating value={rating.presenceScore} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ minWidth: 70 }}>
                        {RATING_CATEGORIES.obscura.icon} Obscura
                      </Typography>
                      <Rating value={rating.obscuraScore} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ minWidth: 70 }}>
                        {RATING_CATEGORIES.trailcraft.icon} Trailcraft
                      </Typography>
                      <Rating value={rating.trailcraftScore} readOnly size="small" />
                    </Box>
                    {rating.craftmarkScore && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
                          {RATING_CATEGORIES.craftmark.icon} Craftmark
                        </Typography>
                        <Rating value={rating.craftmarkScore} readOnly size="small" />
                      </Box>
                    )}
                  </Box>

                  {/* Comment */}
                  {rating.comment && (
                    <Box>
                      {rating.containsSpoilers ? (
                        <Tooltip title="Click to reveal spoiler">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontStyle: 'italic',
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                              borderRadius: 1,
                              p: 1,
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                            onClick={(e) => {
                              const target = e.currentTarget
                              target.style.backgroundColor = 'transparent'
                              target.textContent = `"${rating.comment}"`
                            }}
                          >
                            [Contains spoilers - click to reveal]
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          "{rating.comment}"
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {/* Pagination */}
          {allRatingsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={allRatingsTotalPages}
                page={allRatingsPage}
                onChange={handleAllRatingsPageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllRatingsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Category Info Dialog */}
      <Dialog
        open={!!ratingInfoOpen}
        onClose={() => setRatingInfoOpen(null)}
        maxWidth="xs"
        fullWidth
      >
        {ratingInfoOpen && RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES] && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.5rem' }}>
                {RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES].icon}
              </span>
              {RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES].label}
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES].theme}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES].description}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Scoring Guide
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {Object.entries(RATING_CATEGORIES[ratingInfoOpen as keyof typeof RATING_CATEGORIES].scoringGuide)
                  .sort(([a], [b]) => Number(b) - Number(a))
                  .map(([score, description]) => (
                    <Box key={score} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={Number(score)} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary">
                        {description}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRatingInfoOpen(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Subscription Required Dialog */}
      <Dialog
        open={subscriptionRequiredOpen}
        onClose={() => setSubscriptionRequiredOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lock color="warning" />
          Subscription Required
        </DialogTitle>
        <DialogContent>
          <Typography>
            Group Play is a premium feature available to subscribers. Upgrade your account to play adventures together with friends and other players.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionRequiredOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
